import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Score anything, explain everything',
    description:
      'Users, apps, roles, groups, entitlements: every object carries a configurable score that rolls up into composite risk. And every score decomposes into the factors that produced it.',
    bullets: [
      'Configurable 0-1000 base score + multiplier per object type',
      'Composite risk per user, application, and entitlement',
      'Factor-contribution breakdown, drill-down to events',
      'Base-score suggestions from real access power (blast radius)',
    ],
  },
  {
    title: 'Posture and behavior, one engine',
    description:
      'The industry splits identity risk into two products: posture (ISPM) and behavior (UEBA). Monosign scores both in the same model.',
    bullets: [
      'Posture factors: MFA gaps, dormant accounts, password age, orphans',
      'Behavioral catalog: impossible travel, new device, brute force, off-hours',
      'Cold-start caution for accounts without a baseline',
      'User-reported "this wasn’t me" feeds the model',
    ],
  },
  {
    title: 'Risk that acts at sign-in',
    description:
      'Scores are policy conditions, not dashboard decoration. Risk adds friction where it belongs, and never removes an auth factor, in line with NIST SP 800-63-4.',
    bullets: [
      'Step-up MFA, block, shorten session, or terminate all sessions',
      'Route risky requests to approval workflows',
      'Self-remediation: completing MFA clears active risk',
      'Admin dispositions with reasons, exceptions, and audit',
    ],
  },
  {
    title: 'Risk that governs access',
    description:
      'The same scores drive governance: outliers get flagged, stale access gets surfaced, low-risk reviews approve themselves, and leadership gets a grade.',
    bullets: [
      'Peer-group outlier detection against least-privilege drift',
      'Access dormancy: unused entitlements flagged on a clock',
      'Risk-based auto-certify keeps humans on the risky rows',
      'Identity Security Grade: an A-F report card for the board',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Tune the model',
    description:
      'Set base scores and multipliers per object type, or accept suggested scores computed from real access power.',
  },
  {
    title: 'Wire the policy',
    description:
      'Use risk level as a condition: step-up MFA, block, reauthenticate, or route to approval, per app, per tier.',
  },
  {
    title: 'Watch the grade',
    description:
      'Dashboards track risky users, remediation times, and trends; the A-F grade lands on the board agenda as a PDF.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'ITDR',
    description:
      'The detection and response layer these scores feed.',
    href: '/sign/itdr',
  },
  {
    label: 'Identity intelligence',
    description:
      'The identity graph where risk lands on every node.',
    href: '/sign/identity-intelligence',
  },
  {
    label: 'Use case: identity security posture',
    description:
      'Posture, risk, and threats as one weekly discipline.',
    href: '/solutions/identity-security-posture',
  },
]

export default function SecurityScorePage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity risk scoring & ISPM"
        description="Explainable identity risk scoring: configurable scores on every object, posture + behavioral detections in one engine, risk-based step-up and blocking, peer-group outliers, auto-certification, and an A-F Identity Security Grade."
        path="/sign/security-score"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosign · Security Score"
          title={
            <>
              Identity risk you can read,
              <br />
              tune, and act on.
            </>
          }
          body="Every user, app, and entitlement carries an explainable risk score, built from posture and behavior, enforced at sign-in and in governance. No black boxes: every number decomposes into the factors and events behind it."
          badges={[
            'Explainable, configurable scoring',
            'Posture + behavior in one engine',
            'A-F Identity Security Grade',
          ]}
        />
        <SolutionPillars
          title={<>One risk model, from sign-in to certification.</>}
          body="Auth-time risk engines and governance risk engines are usually separate products. Monosign is both the IdP and the IGA, so one score drives both decisions."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>From default scores to a board-level grade.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
